import { Fragment, memo, useCallback, useMemo, useState, type CSSProperties, type FC } from 'react';
import classNames from 'classnames';
import { Button } from 'antd-mobile';
import { useChannelSupportSafeArea } from '@/hooks/useSupportSafeArea';
import Popup from '../external/Popup';

import styles from './styles/packingfeelist.module.less';
import ProductCardPrice from '../Menu/List/Card/Price';
import { InformationCircleOutline } from 'antd-mobile-icons';

type PackingFeeListItemProps = {
  name?: string;
  price?: number | string;
  totalPrice?: number | string;
  discounts?: boolean;
};

const formatPrice = (price?: number) => (price ? parseFloat(String(price / 100)) : undefined);

const PackingFeeListItem: FC<PackingFeeListItemProps> = memo((props) => {
  const { name, price, totalPrice, discounts } = props;

  const priceText = useMemo(() => (typeof price === 'string' ? price : formatPrice(price)), [price]);
  const totalPriceText = useMemo(
    () => (typeof totalPrice === 'string' ? totalPrice : formatPrice(totalPrice)),
    [totalPrice],
  );

  return (
    <div className={styles.goodsItem}>
      <div className={styles.goodsItemName}>{name}</div>
      <div className={styles.goodsItemUnitPrice}>
        {priceText && (
          <>
            <span>¥{priceText}</span>
            <span style={{ margin: '0 2px' }}>/</span>份
          </>
        )}
      </div>
      <div className={styles.goodsItemTotalPrice} data-discounts={discounts}>
        {totalPriceText && (
          <>
            {discounts && <span>-</span>}
            <span>¥{totalPriceText}</span>
          </>
        )}
      </div>
    </div>
  );
});
PackingFeeListItem.displayName = 'PackingFeeListItem';

export type PackingFeeListProps = {
  visible?: boolean;
  onClose?: () => void;
  style?: CSSProperties;
  className?: string;
  bodyClassName?: string;
  discounts?: { name?: string; amount?: number }[];
  products?: {
    productName?: string;
    name?: string;
    packingFeePrice?: number | string;
    packingFeeTotalPrice?: number | string;
    realPackingFeeTotalPrice?: number | string;
  }[];
  totalPackingFee?: number;
};

const PackingFeeList: FC<PackingFeeListProps> = (props) => {
  const { visible, onClose, style, className, bodyClassName, discounts, products, totalPackingFee } = props;
  const supportSafeArea = useChannelSupportSafeArea();

  console.info(products);

  return (
    <Popup
      style={style}
      className={className}
      destroyOnClose
      bodyClassName={classNames(styles.popupBody, bodyClassName)}
      visible={visible}
      onMaskClick={onClose}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>打包服务费说明</div>
      </div>
      <div className={styles.content}>
        {products
          ?.filter((p) => p.packingFeeTotalPrice || p.realPackingFeeTotalPrice)
          ?.map((p, index) => (
            <PackingFeeListItem
              key={`p${p.name || ''}${index}`}
              name={p.name ?? p.productName}
              price={p.packingFeePrice}
              totalPrice={p.packingFeeTotalPrice ?? p.realPackingFeeTotalPrice}
            />
          ))}
        {discounts?.map((d, index) => (
          <PackingFeeListItem key={`d${d.name || ''}${index}`} name={d.name} totalPrice={d.amount} discounts />
        ))}
      </div>
      <div className={styles.totalFee}>
        <div>合计</div>
        <ProductCardPrice originalPrice={totalPackingFee} />
      </div>
      <div className={styles.footer} data-supportsafearea={supportSafeArea}>
        <Button shape="rounded" color="primary" block data-blod onClick={onClose}>
          确定
        </Button>
      </div>
    </Popup>
  );
};

export type PackingFeeListPopupIcon = Omit<PackingFeeListProps, 'visible' | 'onClose'> & {
  popupStyle?: CSSProperties;
};

export const PackingFeeListPopupIcon: FC<PackingFeeListPopupIcon> = (props) => {
  const { className, style, popupStyle, ...rest } = props;
  const [visible, setVisible] = useState(false);
  const onPackingFeeOpen = useCallback(() => {
    setVisible(true);
  }, []);
  const onPackingFeeClose = useCallback(() => {
    setVisible(false);
  }, []);

  return (
    <Fragment>
      <InformationCircleOutline className={className} style={style} onClick={onPackingFeeOpen} />
      <PackingFeeList style={popupStyle} visible={visible} onClose={onPackingFeeClose} {...rest} />
    </Fragment>
  );
};

export default PackingFeeList;
